import React, { useEffect, useState, useContext, useRef } from "react";
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useWeb3React } from "@web3-react/core";
import { Avatar, Tooltip } from '@nextui-org/react';
import { Popover, Table, Text } from '@nextui-org/react';

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import ContractContext from '../../ContractContext'; //Required for all dApp components using contracts

import { infoStyle, cardStyle, cardHeaderStyle, infoOutlineStyle } from "./TileStyles";

import { formatTokenLabel, formatCurrencyExtended, getTokenPrice, formatTokenIcon, getTokenLogo, chunkArray } from '../../utils';

import BigNumber from 'bignumber.js';

import { Progress } from '@nextui-org/react';

const DashboardModuleProfile = ({ data, getData }) => {

    const { library, account } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;
    const isWalletConnected = () => account !== null;

    const [isClaiming, setIsClaiming] = useState(false);

    const LoadingContent = () => <Skeleton shape="circle" width="50%" height="35px" className="mb-2" />;

    const toast = useRef(null);

    const handleClaim = async () => {
        setIsClaiming(true);
        if (!isWalletConnected()) {
            console.error("Please connect to a wallet before interacting with the contract.");
            return;
        }
        if (!contracts.rp_rewardPacks) {
            console.error("Reward Pack contract not available.");
            return;
        }

        console.log("Reward Pack: Claim Rewards button clicked");

        try {
            const transaction = contracts.rp_rewardPacks.methods.claimAllCurrentDividends().send({ from: account });

            transaction.on("transactionHash", (hash) => {
                toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 3000 });
            });

            const tx = await transaction;

            toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 3000 });

        } catch (error) {
            console.error("Error interacting with the Reward Pack contract:", error);
            toast.current.show({ severity: 'error', summary: 'Transaction Error', detail: `Error interacting with the Reward Pack contract: ${error.message}`, life: 3000 });
        }
        getData();
        setIsClaiming(false);
    };


    const hasUnclaimedRewards = () => {
        if (!data?.contractDataRp?.smartData?.subscriptionPools) return false;

        for (const poolID of data?.contractDataRp?.smartData.subscriptionPools) {
            const currentTokenData = data?.tokenPoolDataRp?.find((token) => token._poolID === poolID, 10);
            if (currentTokenData && currentTokenData._unclaimedAmount > 0) {
                return true;
            }
        }

        return false;
    };

    const unclaimedRewardsCount = () => {
        if (!data?.contractDataRp?.smartData?.subscriptionPools) return 0;

        let total = 0;
        for (const poolID of data?.contractDataRp?.smartData.subscriptionPools) {
            const currentTokenData = data?.tokenPoolDataRp?.find((token) => token._poolID === poolID, 10);
            if (currentTokenData && !isNaN(currentTokenData._unclaimedAmount) && currentTokenData._unclaimedAmount > 0) {
                total += 1;
            }
        }

        return total;
    };



    const getUnclaimedValue = (token) => {
        return formatCurrencyExtended(new BigNumber(token.unclaimedAmount)
            .dividedBy(new BigNumber(10).pow(token.decimals))
            .multipliedBy(getTokenPrice(data.tokensListRp, token.symbol))
            .toString())
    }

    const calculateTotalEarnedUnclaimedByVersion = (tokensList) => {
        let totalEarned = new BigNumber(0);

        if (tokensList) {
            tokensList.forEach(token => {
                if (token.price && (token.unclaimedAmount > 0)) {
                    const value = new BigNumber(token.unclaimedAmount)
                        .times(token.price)
                        .div(new BigNumber(10).pow(token.decimals));

                    totalEarned = totalEarned.plus(value);
                }
            });
        }

        // Convert back to a number, for display
        return totalEarned.isNaN() ? 0 : totalEarned.toNumber();
    };


    return (

        <Card title={<div style={infoOutlineStyle}>Your Reward Pack</div>}
            style={cardStyle}>

            {/* Reward Packs */}

            <div style={infoStyle}>
                {data?.contractDataRp?.smartData
                    ? data.contractDataRp.smartData.subscriptionPools
                        ? <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
                            {chunkArray(data?.contractDataRp?.smartData.subscriptionPools, 5).map((chunk, rowIndex) => (
                                <div key={rowIndex} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
                                    {chunk.map((poolID, index) => {
                                        const currentTokenInfo = data?.dbData?.find((token) => parseInt(token.poolId, 10) === parseInt(poolID, 10));
                                        const currentLogoUrl = getTokenLogo(data?.dbData, currentTokenInfo);
                                        const currentTokenData = data?.tokenPoolDataRp?.find((token) => token._poolID === poolID, 10);

                                        return (currentTokenData &&
                                            <React.Fragment key={index}>
                                                <Tooltip
                                                    content={
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {formatCurrencyExtended(
                                                                    new BigNumber(currentTokenData._unclaimedAmount)
                                                                        .times(getTokenPrice(data.tokensListRp, currentTokenInfo?.symbol))
                                                                        .dividedBy(new BigNumber(10).pow(currentTokenData._decimals))
                                                                )}
                                                                {formatTokenIcon(data?.dbData, currentTokenInfo?.address)}
                                                                {" pending"}
                                                            </div>
                                                            <div>
                                                                <Text small>{currentTokenInfo?.name} / {currentTokenInfo?.symbol}</Text>
                                                            </div>
                                                        </div>
                                                    }
                                                    placement="bottom"
                                                >
                                                    <Avatar
                                                        size="lg"
                                                        pointer
                                                        src={currentLogoUrl}
                                                        bordered
                                                        color={currentTokenData._unclaimedAmount > 0 ? "warning" : "gradient"}
                                                        stacked
                                                        onClick={(e) => { }}
                                                    />
                                                </Tooltip>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                        : "None"
                    : <Skeleton width="100%" height="50px" className="mb-2" />}

            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {
                    hasUnclaimedRewards() &&
                    (
                        isClaiming ? <div style={{ width: '200px', justifyContent: 'center', marginTop: '10px' }}><Progress
                            indeterminated
                            color="gradient"
                            status="secondary"
                            size="xl"

                        /></div> : <>
                            <Button
                                rounded
                                severity="warning"
                                onClick={handleClaim}
                                size="small"
                                style={{

                                    marginTop: '10px',
                                    alignSelf: 'center', // Adjusts the vertical alignment
                                    width: '200px',
                                    justifyContent: 'center',
                                    marginLeft: '10px',
                                    marginRight: '10px',
                                }}
                            >
                                Claim {formatCurrencyExtended(calculateTotalEarnedUnclaimedByVersion(data?.tokensListRp))} ({unclaimedRewardsCount()} Rewards)
                            </Button></>
                    )
                }
            </div>



            <Toast ref={toast} />
        </Card >
    );
}

export default DashboardModuleProfile;
